import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPinIcon, StarIcon, ArrowSquareOutIcon, PhoneIcon, NavigationArrowIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function NearbyServiceDialog({ truck, open, onOpenChange }) {
  const [results, setResults] = useState([]);
  const [nttsUrl, setNttsUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [radius, setRadius] = useState(10000);

  const lat = truck?.latitude;
  const lng = truck?.longitude;

  const search = async () => {
    if (lat == null || lng == null) return;
    setLoading(true);
    setReason("");
    try {
      const { data } = await api.get(`/services/nearby?lat=${lat}&lng=${lng}&radius_m=${radius}`);
      if (!data.ok) setReason(data.reason || "Search failed");
      setResults(data.results || []);
      setNttsUrl(data.ntts_url || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && lat != null && lng != null) {
      search();
    } else if (open) {
      setResults([]);
      setNttsUrl("");
    }
  }, [open, truck?.id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-hidden flex flex-col" data-testid="nearby-service-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <NavigationArrowIcon size={18} weight="bold" className="text-amber-700" /> Find nearby service
          </DialogTitle>
        </DialogHeader>

        {lat == null || lng == null ? (
          <div className="space-y-4">
            <div className="text-sm text-slate-700">
              This truck doesn't have a current GPS location yet. You can still open the NTTS directory to search manually by city / highway / state.
            </div>
            <a
              href="https://www.nttsbreakdown.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block border-2 border-amber-300 bg-amber-50 hover:bg-amber-100 rounded-sm p-4 transition-colors duration-150"
              data-testid="ntts-deeplink"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display font-black text-base tracking-tight">Open NTTS Breakdown Directory</div>
                  <div className="text-xs text-slate-700 mt-0.5">Search the full directory at nttsbreakdown.com →</div>
                </div>
                <ArrowSquareOutIcon size={20} className="text-amber-700" />
              </div>
            </a>
            <div className="text-xs text-slate-500 font-mono border-t border-slate-200 pt-3">
              Tip: link this truck to Motive to enable lat/lng-based search and Google Places results.
            </div>
          </div>
        ) : (
          <div className="space-y-4 overflow-y-auto pr-2 scrollbar-thin">
            <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-xs font-mono">
              <div>Truck location: <span className="font-bold">{lat.toFixed(5)}, {lng.toFixed(5)}</span></div>
              {truck?.location_address && <div className="mt-0.5 text-slate-600">{truck.location_address}</div>}
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Radius</Label>
              <Input type="number" value={radius / 1000} onChange={(e) => setRadius(Math.max(1, Number(e.target.value)) * 1000)} className="w-24 rounded-sm" data-testid="nearby-radius" />
              <span className="text-xs text-slate-500">km</span>
              <Button onClick={search} disabled={loading} className="bg-slate-900 hover:bg-slate-800 rounded-sm ml-auto" data-testid="nearby-search">
                {loading ? "Searching..." : "Search"}
              </Button>
            </div>

            {nttsUrl && (
              <a
                href={nttsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block border-2 border-amber-300 bg-amber-50 hover:bg-amber-100 rounded-sm p-4 transition-colors duration-150"
                data-testid="ntts-deeplink"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display font-black text-base tracking-tight">NTTS Breakdown Directory</div>
                    <div className="text-xs text-slate-700 mt-0.5">Open the full NTTS search for this location →</div>
                  </div>
                  <ArrowSquareOutIcon size={20} className="text-amber-700" />
                </div>
              </a>
            )}

            {reason && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2" data-testid="nearby-reason">
                Google Places: {reason}. Configure your Google Maps API key in Integrations to enable nearby search.
              </div>
            )}

            {results.length > 0 ? (
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
                  /// {results.length} Google Places result{results.length === 1 ? "" : "s"}
                </div>
                <div className="space-y-2">
                  {results.map((r) => (
                    <a
                      key={r.place_id}
                      href={r.maps_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block border border-slate-200 rounded-sm p-3 hover:border-blue-400 hover:bg-blue-50 transition-colors duration-150"
                      data-testid={`nearby-result-${r.place_id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm text-slate-900">{r.name}</div>
                          <div className="text-xs text-slate-600 mt-0.5">{r.address}</div>
                          <div className="flex items-center gap-3 mt-1 text-xs">
                            {r.rating != null && (
                              <span className="flex items-center gap-1 text-amber-700 font-semibold">
                                <StarIcon size={12} weight="fill" /> {r.rating} <span className="text-slate-500 font-normal">({r.user_ratings_total})</span>
                              </span>
                            )}
                            {r.open_now === true && <span className="text-green-700 font-semibold">Open now</span>}
                            {r.open_now === false && <span className="text-red-700 font-semibold">Closed</span>}
                          </div>
                        </div>
                        <ArrowSquareOutIcon size={16} className="text-slate-400 flex-shrink-0 mt-1" />
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ) : (
              !loading && !reason && (
                <div className="text-xs text-slate-500 font-mono text-center py-4">No Google Places results in this radius.</div>
              )
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
